/**
 * Resolve human-readable labels for path lander/offer rows from Voluum GET /flow.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Names often live on the weighted row (landerName, offerName, namePostfix)
 * rather than inside `lander` / `offer` — mirrors backend voluum-flow.mapper.
 */

export type PathItemKind = 'landers' | 'offers';

type ItemRow = Record<string, unknown>;
type EntityRef = Record<string, unknown> | undefined;

function pickStr(...candidates: unknown[]): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  return null;
}

function refKey(kind: PathItemKind): 'lander' | 'offer' {
  return kind === 'landers' ? 'lander' : 'offer';
}

function getRef(item: ItemRow, kind: PathItemKind): EntityRef {
  const key = refKey(kind);
  const v = item[key];
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

function getNestedId(ref: EntityRef): string | null {
  if (!ref) return null;
  return pickStr(ref.id);
}

function getEntityId(row: ItemRow, kind: PathItemKind): string {
  const key = refKey(kind);
  const v = row[key];
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  return getNestedId(getRef(row, kind)) ?? '—';
}

/** Display title + optional id for tooltips. */
export function getPathItemDisplay(
  item: unknown,
  kind: PathItemKind,
): { id: string; title: string; subtitle: string } {
  const row = item && typeof item === 'object' ? (item as ItemRow) : {};
  const ref = getRef(row, kind);
  const id = getEntityId(row, kind);

  const fromRef = pickStr(ref?.name, ref?.namePostfix);
  const itemLevel =
    kind === 'landers'
      ? pickStr(row.landerName, row.name, row.namePostfix)
      : pickStr(row.offerName, row.name, row.namePostfix);

  const title = pickStr(fromRef, itemLevel) ?? id;
  const subtitle = id !== title ? id : '';

  return { id, title, subtitle };
}
